import React, { useState, useEffect } from 'react';
import './AdminWithdrawals.css';

const AdminWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadWithdrawals = () => {
      const savedWithdrawals = localStorage.getItem('adminWithdrawals');
      if (savedWithdrawals) {
        try {
          const withdrawals = JSON.parse(savedWithdrawals);
          setWithdrawals(withdrawals);
        } catch (e) {
          console.error('Error loading withdrawals:', e);
          setWithdrawals([]);
        }
      } else {
        setWithdrawals([]);
      }
    };

    loadWithdrawals();
    
    // Poll for updates
    const interval = setInterval(loadWithdrawals, 3000);
    return () => clearInterval(interval);
  }, []);

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesFilter = filter === 'all' || w.status === filter;
    const matchesSearch = 
      w.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.id.toString().includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const handleApprove = (withdrawalId) => {
    const updatedWithdrawals = withdrawals.map(w => {
      if (w.id === withdrawalId) {
        return {
          ...w,
          status: 'approved',
          approvedAt: new Date().toISOString(),
        };
      }
      return w;
    });
    setWithdrawals(updatedWithdrawals);
    localStorage.setItem('adminWithdrawals', JSON.stringify(updatedWithdrawals));
  };

  const handleReject = (withdrawalId) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      const updatedWithdrawals = withdrawals.map(w => {
        if (w.id === withdrawalId) {
          return {
            ...w,
            status: 'rejected',
            rejectedAt: new Date().toISOString(),
            rejectionReason: reason,
          };
        }
        return w;
      });
      setWithdrawals(updatedWithdrawals);
      localStorage.setItem('adminWithdrawals', JSON.stringify(updatedWithdrawals));
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
        return 'status-pending';
      default:
        return '';
    }
  };

  return (
    <div className="admin-withdrawals">
      <div className="admin-withdrawals-header">
        <div className="admin-withdrawals-filters">
          <button
            className={`admin-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`admin-filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`admin-filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={`admin-filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
        </div>
        <div className="admin-withdrawals-search">
          <input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
        </div>
      </div>

      <div className="admin-withdrawals-table-container">
        <table className="admin-withdrawals-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Details</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWithdrawals.length === 0 ? (
              <tr>
                <td colSpan="8" className="admin-table-empty">
                  No withdrawals found
                </td>
              </tr>
            ) : (
              filteredWithdrawals.map((withdrawal) => (
                <tr key={withdrawal.id}>
                  <td className="admin-table-id">#{withdrawal.id}</td>
                  <td>
                    <div className="admin-table-user">
                      <div className="admin-table-user-name">{withdrawal.userName}</div>
                      <div className="admin-table-user-email">{withdrawal.userEmail}</div>
                    </div>
                  </td>
                  <td className="admin-table-amount">
                    {formatCurrency(withdrawal.amount)}
                    <span className="admin-table-fee">Fee: {formatCurrency(withdrawal.fee)}</span>
                  </td>
                  <td className="admin-table-method">
                    <span className={`method-badge method-${withdrawal.method}`}>
                      {withdrawal.method === 'crypto' ? 'Crypto' : 
                       withdrawal.method === 'bank' ? 'Bank' : 'M-Pesa'}
                    </span>
                  </td>
                  <td className="admin-table-details">
                    {withdrawal.method === 'crypto' && (
                      <div>
                        <div><strong>Network:</strong> {withdrawal.network}</div>
                        <div><strong>Wallet:</strong> {withdrawal.walletAddress}</div>
                      </div>
                    )}
                    {withdrawal.method === 'bank' && (
                      <div>
                        <div><strong>Bank:</strong> {withdrawal.bankName}</div>
                        <div><strong>Account:</strong> {withdrawal.accountNumber}</div>
                        <div><strong>Name:</strong> {withdrawal.accountName}</div>
                      </div>
                    )}
                    {withdrawal.method === 'mpesa' && (
                      <div>
                        <div><strong>Phone:</strong> {withdrawal.phoneNumber}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`admin-status-badge ${getStatusBadgeClass(withdrawal.status)}`}>
                      {withdrawal.status}
                    </span>
                  </td>
                  <td className="admin-table-date">{formatDate(withdrawal.submittedAt)}</td>
                  <td className="admin-table-actions">
                    {withdrawal.status === 'pending' && (
                      <>
                        <button
                          className="admin-action-btn admin-action-approve"
                          onClick={() => handleApprove(withdrawal.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="admin-action-btn admin-action-reject"
                          onClick={() => handleReject(withdrawal.id)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {withdrawal.status === 'rejected' && withdrawal.rejectionReason && (
                      <div className="admin-rejection-reason">
                        <strong>Reason:</strong> {withdrawal.rejectionReason}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminWithdrawals;

